/**
 * Hariom ERP - Quality Control Module
 * QC test reports (IS:1786 / IS:13620). Entered, edited and deleted by QC
 * staff and persisted.
 */
import React, { useState } from 'react'
import { Plus, CheckCircle2, XCircle, AlertTriangle, Edit3, Trash2 } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useQCStore, type QCTest } from '../../store/qcStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'

const RESULT_CONFIG: Record<string, { cls: string; icon: React.ReactNode }> = {
  passed:      { cls: 'badge-success', icon: <CheckCircle2 size={14} className="text-green-600"/> },
  failed:      { cls: 'badge-danger',  icon: <XCircle      size={14} className="text-red-600"/>   },
  conditional: { cls: 'badge-warning', icon: <AlertTriangle size={14} className="text-yellow-600"/>},
  pending:     { cls: 'badge-info',    icon: <AlertTriangle size={14} className="text-blue-600"/>  },
}

const STANDARDS = ['IS:1786', 'IS:13620', 'IS:13239', 'ASTM A775']
const RESULTS = ['passed', 'failed', 'conditional', 'pending']

const TEST_FIELDS: FormField[] = [
  { name: 'batch',    label: 'Batch No', required: true, placeholder: 'e.g. BTH-2026-035' },
  { name: 'product',  label: 'Product', required: true, placeholder: 'e.g. Epoxy TMT 12mm' },
  { name: 'testType', label: 'Test Type', placeholder: 'e.g. Tensile Strength', full: true },
  { name: 'standard', label: 'Standard', type: 'select', options: STANDARDS },
  { name: 'result',   label: 'Result', type: 'select', options: RESULTS },
  { name: 'measured', label: 'Measured Value', type: 'number', placeholder: '0' },
  { name: 'unit',     label: 'Unit', placeholder: 'e.g. MPa' },
  { name: 'reqMin',   label: 'Required Min', type: 'number', placeholder: '0' },
  { name: 'reqMax',   label: 'Required Max', type: 'number', placeholder: '0' },
  { name: 'date',     label: 'Test Date', type: 'date' },
  { name: 'by',       label: 'Tested By', placeholder: 'e.g. QC Lab A' },
]

const num = (v: string) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0 }

const QualityControl: React.FC = () => {
  const { items: tests, add, update, remove } = useQCStore()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<QCTest | null>(null)

  const passRate = tests.length ? Math.round((tests.filter(t => t.result === 'passed').length / tests.length) * 100) : 0

  const handleSave = (v: Record<string, string>) => {
    const record: QCTest = {
      id: editing ? editing.id : `qc-${Date.now()}`,
      batch: v.batch.trim(), product: v.product.trim(), testType: v.testType.trim(),
      standard: v.standard || 'IS:1786', result: v.result || 'pending',
      measured: num(v.measured), reqMin: num(v.reqMin), reqMax: num(v.reqMax),
      unit: v.unit.trim(), date: v.date, by: v.by.trim(),
    }
    if (editing) { update(editing.id, record); toast.success(`Updated ${record.batch}`) }
    else { add(record); toast.success(`Test report added`) }
    setModalOpen(false); setEditing(null)
  }

  const handleDelete = (t: QCTest) => {
    if (window.confirm(`Delete test ${t.batch}?`)) { remove(t.id); toast.success('Deleted') }
  }

  const toRecord = (t: QCTest): Record<string, string> => ({
    batch: t.batch, product: t.product, testType: t.testType, standard: t.standard,
    result: t.result, measured: String(t.measured), unit: t.unit,
    reqMin: String(t.reqMin), reqMax: String(t.reqMax), date: t.date, by: t.by,
  })

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Quality Control</h1>
          <p className="page-subtitle">IS:1786 &amp; IS:13620 standards compliance tracking</p>
        </div>
        <button onClick={() => { setEditing(null); setModalOpen(true) }} className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> New Test Report</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Total Tests</p><p className="text-2xl font-bold">{tests.length}</p></div>
        <div className="card py-3 border-l-4 border-l-green-500"><p className="text-xs text-steel-500">Passed</p><p className="text-2xl font-bold text-green-600">{tests.filter(t => t.result === 'passed').length}</p></div>
        <div className="card py-3 border-l-4 border-l-red-500"><p className="text-xs text-steel-500">Failed</p><p className="text-2xl font-bold text-red-600">{tests.filter(t => t.result === 'failed').length}</p></div>
        <div className="card py-3 border-l-4 border-l-primary-500"><p className="text-xs text-steel-500">Pass Rate</p><p className="text-2xl font-bold">{passRate}%</p></div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-steel-50 border-b border-steel-200">
              <th className="table-header">Batch No</th><th className="table-header">Product</th>
              <th className="table-header">Test Type</th><th className="table-header">Standard</th>
              <th className="table-header text-center">Measured</th><th className="table-header text-center">Required Range</th>
              <th className="table-header text-center">Result</th><th className="table-header">Date</th>
              <th className="table-header">Tested By</th><th className="table-header text-center">Actions</th>
            </tr></thead>
            <tbody>
              {tests.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-steel-400 text-sm py-10">
                  No test reports yet. Click <span className="font-semibold text-steel-600">“New Test Report”</span> to add one.
                </td></tr>
              ) : tests.map(t => {
                const rc = RESULT_CONFIG[t.result] || RESULT_CONFIG.pending
                return (
                  <tr key={t.id} className={clsx('table-row-hover', t.result === 'failed' && 'bg-red-50/40', t.result === 'conditional' && 'bg-yellow-50/40')}>
                    <td className="table-cell font-mono text-xs font-medium">{t.batch}</td>
                    <td className="table-cell text-sm font-medium">{t.product}</td>
                    <td className="table-cell text-sm">{t.testType || '—'}</td>
                    <td className="table-cell"><span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{t.standard}</span></td>
                    <td className="table-cell text-center font-bold">{t.measured} {t.unit}</td>
                    <td className="table-cell text-center text-xs text-steel-500">{t.reqMin}–{t.reqMax} {t.unit}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">{rc.icon}<span className={rc.cls}>{t.result}</span></div>
                    </td>
                    <td className="table-cell text-xs">{t.date || '—'}</td>
                    <td className="table-cell text-sm">{t.by || '—'}</td>
                    <td className="table-cell text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => { setEditing(t); setModalOpen(true) }} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={14}/></button>
                        <button onClick={() => handleDelete(t)} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <FormModal
          title={editing ? 'Edit Test Report' : 'New Test Report'}
          submitLabel={editing ? 'Save Changes' : 'Add Report'}
          fields={TEST_FIELDS}
          initial={editing ? toRecord(editing) : {}}
          onClose={() => { setModalOpen(false); setEditing(null) }}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default QualityControl
